import { useEffect, useRef, useState } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import type {
  ConsoleStats,
  CustomerRow,
  LogEntry,
  ResumeSnapshotDto,
  ResumeStateDto,
  RunLogLineDto,
  RunStateDto,
  SelfMetricsDto,
  ServerEvent,
  SessionSummary,
} from '@acs/shared'
import { API_BASE } from '@/lib/api'
import { noteRunEvent } from '@/lib/active-hits'
import {
  playRunEvent,
  refreshFleetSounds,
  syncFleetSounds,
  wantsEvents as sfxWantsEvents,
} from '@/lib/sfx'
import { RUNS_KEY, runLogKey, type RunLogResponse, type RunStateMap } from '@/hooks/use-automation'
import { applyCustomerPatches } from '@/hooks/use-customers'
import { RESUME_KEY } from '@/hooks/use-resume'
import { SELF_HISTORY_LIMIT, SELF_METRICS_KEY } from '@/hooks/use-system-metrics'

/**
 * The console's live wire.
 *
 * Mounted once, near the root. It owns the `EventSource`, merges every
 * `ServerEvent` directly into the TanStack Query cache, and exposes only the
 * connection health that the status indicator needs.
 *
 * Two rules shape the whole file:
 *
 *  - **Never refetch in response to an event.** A patch carries the new
 *    values; asking the API for them again would turn one push into 600 rows
 *    of HTTP. If a patched row is not in the current view (filtered out), the
 *    event is dropped, not escalated to an invalidation.
 *  - **Never touch the cache more than once per frame.** See BATCHING below.
 */

export interface RealtimeStatus {
  connected: boolean
  lastEventAt: number | null
  reconnectAttempt: number
}

const BASE_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000

/** `lastEventAt` drives a "…s ago" readout; a second of resolution is plenty. */
const LAST_EVENT_COMMIT_MS = 1_000

/**
 * Bound on the in-memory tail of one RUN's log.
 *
 * Matches the server's ring (`HIRING_RUN_LOG_LIMIT`, 600 by default) with room
 * to spare, so a console that has been open across several runs still holds
 * more than the server would ever hand back on a re-seed.
 *
 * Equal to `MAX_SEEDED_LINES` in `use-automation.ts`, and it has to stay that
 * way: the seed path trims the merged view to that number, so a larger bound
 * here would only hold lines until the next delta fetch discarded them.
 */
const MAX_RUN_LINES = 1_200

/** Named SSE frames we accept in addition to unnamed `data:` frames. */
const EVENT_NAMES = [
  'hello',
  'heartbeat',
  'customer.patch',
  'stats',
  'run.append',
  'run.state',
  'resume.state',
  'sounds.changed',
  'system.self',
] as const

/* ------------------------------------------------------------------ *
 * BATCHING
 *
 * Events do not arrive one at a time. A worker fleet stepping 600 customers
 * through a pipeline produces bursts of hundreds of `customer.patch` frames
 * in the same tick, and several of those frames are usually for the *same*
 * customer (step change, then heartbeat, then status). Calling setQueryData
 * per frame would mean hundreds of cache writes and hundreds of React render
 * passes for a screen that can only show one.
 *
 * So: every event lands in this buffer keyed by entity — later patches
 * Object.assign over earlier ones, collapsing a burst into one patch per
 * customer/session — and a single requestAnimationFrame flush applies the
 * whole batch in one pass, at most once per painted frame.
 * ------------------------------------------------------------------ */

interface EventBuffer {
  customers: Map<string, Partial<CustomerRow>>
  /** customerId → run-log lines appended since the last frame. */
  runLines: Map<string, RunLogLineDto[]>
  /** customerId → the latest run state. Later frames replace earlier ones. */
  runStates: Map<string, RunStateDto>
  /**
   * Self-metric samples received since the last frame. An ARRAY, unlike every
   * other entry here, because these are points on a chart rather than the
   * current state of a thing — collapsing a burst to its last member would put
   * a gap in the line instead of saving a render.
   */
  selfMetrics: SelfMetricsDto[]
  stats: ConsoleStats | null
  /**
   * The resume runner's latest state. One slot, not a map: there is one runner,
   * and each frame is a complete snapshot, so a burst collapses to its newest.
   */
  resumeState: ResumeStateDto | null
  /** Local receipt time — not the server's `at`, which can be skewed. */
  at: number | null
}

function emptyBuffer(): EventBuffer {
  return {
    customers: new Map(),
    runLines: new Map(),
    runStates: new Map(),
    selfMetrics: [],
    stats: null,
    resumeState: null,
    at: null,
  }
}

/* ------------------------------------------------------------------ *
 * Parsing
 * ------------------------------------------------------------------ */

/**
 * `fallbackType` covers servers that put the discriminator in the SSE
 * `event:` field instead of (or as well as) the JSON body.
 */
function parseEvent(raw: unknown, fallbackType?: string): ServerEvent | null {
  if (typeof raw !== 'string' || raw === '') return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null

  const obj = parsed as Record<string, unknown>
  const type = typeof obj['type'] === 'string' ? (obj['type'] as string) : fallbackType
  if (!type) return null
  return { ...obj, type } as ServerEvent
}

/* ------------------------------------------------------------------ *
 * Cache merges
 * ------------------------------------------------------------------ */

/*
 * `applyCustomerPatches` lived here, and the table kept a copy of it without
 * the detail-cache half. Both now come from `hooks/use-customers`, so the
 * stream, the table's in-place edits and the drawer's write through the same
 * merge — see the note there.
 */

/*
 * `applySessionPatches`, `applyLogAppends` and `isNewestFirst` lived here.
 *
 * They merged the sibling fleet's `customer_sessions` rows and `session_logs`
 * lines into the cache. The server sends neither frame any more, so there is
 * nothing to merge — this console's own runs arrive as `run.append` and
 * `run.state` below.
 */

/* ------------------------------------------------------------------ *
 * The live run
 * ------------------------------------------------------------------ */

/**
 * Append run-log lines to whichever consoles are holding that customer.
 *
 * The updater returns `prev` untouched when the console was never opened, and
 * `setQueryData` then does nothing — so lines for the other 599 customers cost
 * one map lookup each and never create a phantom cache entry.
 *
 * De-duplicated by `(at, message)` rather than an id because these lines have no
 * id: they are ring entries, not documents. The pair is unique in practice —
 * two identical messages in the same millisecond for one customer would be the
 * same line — and the check only runs against the tail, not the whole array.
 */
function applyRunAppends(qc: QueryClient, appends: Map<string, RunLogLineDto[]>): void {
  for (const [customerId, lines] of appends) {
    if (lines.length === 0) continue

    qc.setQueryData<RunLogResponse>(runLogKey(customerId), (prev) => {
      if (!prev) return prev

      // Only the tail can collide: the seed fetch and the stream race over the
      // most recent lines, never over old ones.
      const tail = prev.lines.slice(-lines.length * 2)
      const seen = new Set(tail.map((l) => `${l.at}|${l.message}`))
      const fresh = lines.filter((l) => !seen.has(`${l.at}|${l.message}`))
      if (fresh.length === 0) return prev

      const merged = [...prev.lines, ...fresh]
      return {
        ...prev,
        lines:
          merged.length > MAX_RUN_LINES ? merged.slice(merged.length - MAX_RUN_LINES) : merged,
        updatedAt: fresh[fresh.length - 1]?.at ?? prev.updatedAt,
      }
    })
  }
}

/**
 * Merge run states into the table's map and into any open console.
 *
 * ONE `setQueryData` for the whole batch, not one per customer: this key backs
 * every row in the table, so writing it per state would re-render the grid once
 * per customer in a burst rather than once for the burst.
 */
/**
 * The resume runner's progress.
 *
 * Update-only, for the same reason `applyRunStates` refuses to create the run
 * map: the seed GET carries the two ROSTERS as well as the runner, and a frame
 * that created this query would leave the bar rendering a run with no rosters
 * behind it — and, because the seed is `staleTime`-guarded, never fetching the
 * half it is missing. A frame arriving before the seed is safe to drop: the
 * next one is a complete snapshot too.
 */
function applyResumeState(qc: QueryClient, run: ResumeStateDto): void {
  qc.setQueryData<ResumeSnapshotDto>(RESUME_KEY, (prev) =>
    prev ? { ...prev, run, at: Date.now() } : prev
  )
}

function applyRunStates(qc: QueryClient, states: Map<string, RunStateDto>): void {
  if (states.size === 0) return

  /**
   * A phase change moves a customer between `liveState` buckets on the server —
   * see `resolveLiveState` — so it changes which rows a `?live=` filter returns
   * and what the rail's Sessions count is. Neither is derivable in the browser:
   * the filter and the counts are both evaluated against the whole collection.
   *
   * Keyed on the PHASE and nothing else. State frames arrive continuously while
   * a run works — every counter tick is one — and invalidating on each would put
   * the customer list and a `$facet` over the whole collection behind a firehose.
   * Phase transitions are a handful per run.
   */
  let phaseChanged = false

  qc.setQueryData<RunStateMap>(RUNS_KEY, (prev) => {
    /*
     * Never CREATE this query — only update one that already exists.
     *
     * `setQueryData` on a key with no entry builds the query and stamps
     * `dataUpdatedAt`. `useRunStates` is `staleTime: Infinity` with
     * `refetchOnMount: false`, so on mount it sees data already present and
     * never fetches the seed at all: the table would then know only about
     * customers that happened to emit a frame since the socket opened.
     *
     * That is the precise failure the run map exists to prevent — an
     * already-running customer with no entry reads as startable, and a second
     * click sends them a second verification code. Frames that arrive before
     * the seed are safe to drop instead, because `/automation/runs` returns
     * EVERY tracked run and is therefore authoritative on arrival.
     */
    if (!prev) return prev
    const next: RunStateMap = { ...prev }
    for (const [customerId, state] of states) {
      if (prev[customerId]?.phase !== state.phase) phaseChanged = true
      next[customerId] = state
    }
    return next
  })

  if (phaseChanged) {
    void qc.invalidateQueries({ queryKey: ['customers'] })
    void qc.invalidateQueries({ queryKey: ['stats'] })
  }

  // The console header reads its state from its own query, so it is kept in
  // step here rather than made to subscribe to the map and filter it.
  for (const [customerId, state] of states) {
    qc.setQueryData<RunLogResponse>(runLogKey(customerId), (prev) =>
      prev ? { ...prev, state } : prev
    )
  }
}

/* ------------------------------------------------------------------ *
 * Self-metrics
 * ------------------------------------------------------------------ */

/**
 * Append pushed samples to the rolling window behind the System view.
 *
 * Unlike every other merge in this file this one CREATES its key if absent: the
 * stream is the only writer there is, so refusing to create would mean the
 * window stayed empty forever on any console that mounted the chart after the
 * socket opened. The rule the run-state merge follows — never create, because a
 * seed fetch is authoritative — has no equivalent here because there is no seed
 * fetch to lose.
 */
function applySelfMetrics(qc: QueryClient, samples: SelfMetricsDto[]): void {
  if (samples.length === 0) return

  qc.setQueryData<SelfMetricsDto[]>(SELF_METRICS_KEY, (prev) => {
    const incoming = samples[samples.length - 1]
    if (!incoming) return prev

    /*
     * A different `instanceId` means the API restarted (or a load balancer
     * moved this console to another replica) and the two halves are not one
     * series: uptime resets to zero, and the CPU baseline belongs to a process
     * that no longer exists. Splicing them would draw a cliff that looks like
     * an incident. Start the window over instead.
     */
    const base =
      prev && prev.length > 0 && prev[prev.length - 1]?.instanceId === incoming.instanceId
        ? prev
        : []

    // Monotonic by `at`: a duplicate or out-of-order frame would otherwise draw
    // the line briefly backwards.
    const newestKept = base[base.length - 1]?.at ?? 0
    const fresh = samples.filter((s) => s.at > newestKept && s.instanceId === incoming.instanceId)
    if (fresh.length === 0) return base === prev ? prev : base

    const merged = [...base, ...fresh]
    return merged.length > SELF_HISTORY_LIMIT
      ? merged.slice(merged.length - SELF_HISTORY_LIMIT)
      : merged
  })
}

/* ------------------------------------------------------------------ *
 * Reconnect
 * ------------------------------------------------------------------ */

function backoffDelay(attempt: number): number {
  const capped = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.max(0, attempt - 1))
  // ±25% jitter: after an API restart every open console would otherwise
  // reconnect on the same tick and knock it over again.
  return Math.round(capped * (0.75 + Math.random() * 0.5))
}

/* ------------------------------------------------------------------ *
 * Hook
 * ------------------------------------------------------------------ */

export function useRealtime(): RealtimeStatus {
  const queryClient = useQueryClient()

  const [connected, setConnected] = useState(false)
  const [lastEventAt, setLastEventAt] = useState<number | null>(null)
  const [reconnectAttempt, setReconnectAttempt] = useState(0)

  const bufferRef = useRef<EventBuffer>(emptyBuffer())
  const frameRef = useRef<number | null>(null)
  const committedAtRef = useRef(0)
  /**
   * The last running total seen on a `stats` frame — `-1` until the first one,
   * so the very first frame always refreshes the resume snapshot rather than
   * matching a zero the fleet might genuinely be at.
   */
  const runningSumRef = useRef(-1)
  /** False until the stream has opened once, so the first open does not refetch. */
  const hasConnectedBefore = useRef(false)

  useEffect(() => {
    let disposed = false
    let source: EventSource | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0

    /*
     * Pull the fleet's uploaded sounds once per console session, from the
     * same effect that opens the stream — this hook mounts exactly once, in
     * the authenticated shell, which is also the earliest moment the request
     * can succeed. After this, `sounds.changed` frames keep it current; the
     * call itself is quiet about every failure and cheap when nothing is
     * stored.
     */
    void refreshFleetSounds()

    const flush = (): void => {
      frameRef.current = null
      const buffer = bufferRef.current
      bufferRef.current = emptyBuffer()

      applyCustomerPatches(queryClient, buffer.customers)
      applyRunAppends(queryClient, buffer.runLines)
      applyRunStates(queryClient, buffer.runStates)
      applySelfMetrics(queryClient, buffer.selfMetrics)
      if (buffer.stats) {
        queryClient.setQueryData<ConsoleStats>(['stats'], buffer.stats)
        /*
         * The resume bar's "Save all running (N)" is a SERVER count, so it can
         * only move when the server refetches it — and the moment it needs to
         * move is exactly this one: the running population changed.
         *
         * Gated on the sum actually changing rather than firing on every stats
         * frame. Stats are pushed whenever any number moves, including won and
         * active-hit counters that leave the running set alone, and a refetch
         * per frame would put a read behind a firehose during a bulk start.
         */
        const running = buffer.stats.live.live + buffer.stats.live.starting + buffer.stats.live.stale
        if (running !== runningSumRef.current) {
          runningSumRef.current = running
          void queryClient.invalidateQueries({ queryKey: RESUME_KEY })
        }
      }
      if (buffer.resumeState) applyResumeState(queryClient, buffer.resumeState)

      // Throttled: this is state, so every commit re-renders the status
      // indicator. Once a second is all a "…s ago" readout can show.
      if (buffer.at !== null && buffer.at - committedAtRef.current >= LAST_EVENT_COMMIT_MS) {
        committedAtRef.current = buffer.at
        if (!disposed) setLastEventAt(buffer.at)
      }
    }

    const scheduleFlush = (): void => {
      if (frameRef.current !== null) return
      // In a hidden tab rAF never fires, so the buffer simply keeps
      // coalescing — bounded, because it is keyed by entity and the log
      // arrays are capped — and drains on the first frame after the tab
      // comes back. Focus also triggers React Query's refetch as a backstop.
      frameRef.current = requestAnimationFrame(flush)
    }

    const ingest = (event: ServerEvent): void => {
      const buffer = bufferRef.current
      buffer.at = Date.now()

      switch (event.type) {
        case 'customer.patch': {
          const prev = buffer.customers.get(event.id)
          buffer.customers.set(event.id, prev ? { ...prev, ...event.patch } : { ...event.patch })
          break
        }
        // `session.patch` and `log.append` used to be handled here. The server
        // no longer sends either: they carried the sibling fleet's sessions and
        // log lines, and every console received all of them to discard them.
        case 'run.append': {
          /*
           * Announced from INGEST, not from the frame flush below.
           *
           * The flush waits on `requestAnimationFrame`, which does not run in a
           * hidden tab — so a console in a background tab would coalesce a
           * whole minute of openings and then play all of them at once the
           * moment it came forward. Here every event is heard when it arrives,
           * a backgrounded tab still reports, and there is no backlog that
           * would need a staleness rule to throw away.
           *
           * Nothing here touches the query cache or React: the sound module
           * and the ACTIVE-hit tracker both keep their own state, and lines
           * without an event tag — the overwhelming majority — cost one
           * property read each.
           *
           * The customer id is passed because it PLACES the sound: each
           * customer keeps a fixed spot in the stereo field and a fixed few
           * cents of pitch, which is what lets ten simultaneous events be heard
           * as ten rather than as one loud one.
           *
           * The tracker is fed UNCONDITIONALLY — the table's highlight is
           * wanted with the console muted too, so it does not sit behind the
           * sound switch. Its per-call cost is a string compare and, for the
           * four events it cares about, a Map write; everything visible it
           * does is deferred to a coalesced animation frame.
           */
          const wantSfx = sfxWantsEvents()
          for (const line of event.lines) {
            if (!line.event) continue
            if (wantSfx) playRunEvent(line.event, event.customerId)
            noteRunEvent(line.event, event.customerId)
          }

          const list = buffer.runLines.get(event.customerId)
          if (!list) {
            buffer.runLines.set(event.customerId, [...event.lines])
          } else {
            list.push(...event.lines)
            if (list.length > MAX_RUN_LINES) list.splice(0, list.length - MAX_RUN_LINES)
          }
          break
        }
        case 'run.state': {
          // Replaced, not merged: a state frame is a complete snapshot, so the
          // newest one in a burst is the only one worth keeping.
          buffer.runStates.set(event.state.customerId, event.state)
          break
        }
        case 'resume.state': {
          // Same rule, one slot: there is a single runner and each frame carries
          // its whole state, so a burst collapses to the newest.
          buffer.resumeState = event.state
          break
        }
        case 'sounds.changed': {
          /*
           * Handed straight to the sound module, not buffered: the frame is
           * rare (someone uploaded or removed a fleet sound), it carries a
           * complete snapshot so ordering cannot corrupt anything, and the
           * module coalesces overlapping syncs itself. Within seconds every
           * open console is playing the new file — no refresh, no polling.
           */
          void syncFleetSounds(event.sounds)
          break
        }
        case 'system.self': {
          buffer.selfMetrics.push(event.self)
          /*
           * Bounded here as well as at the merge, and that is not belt-and-
           * braces — it is the only bound that applies in a HIDDEN tab, where
           * rAF never fires and this buffer is not drained at all. Every other
           * entry is a keyed map that coalesces; an append-only array left
           * unbounded would grow for as long as the tab stays in the background.
           */
          if (buffer.selfMetrics.length > SELF_HISTORY_LIMIT) {
            buffer.selfMetrics.splice(0, buffer.selfMetrics.length - SELF_HISTORY_LIMIT)
          }
          break
        }
        case 'stats': {
          buffer.stats = event.stats
          break
        }
        case 'hello':
        case 'heartbeat':
          // Liveness only — the flush picks up `buffer.at`.
          break
        default:
          // Unknown frame from a newer API: ignore, still counts as liveness.
          break
      }

      scheduleFlush()
    }

    const handleFrame = (raw: unknown, fallbackType?: string): void => {
      const event = parseEvent(raw, fallbackType)
      if (!event) return
      ingest(event)
    }

    const teardownSource = (): void => {
      if (!source) return
      source.onopen = null
      source.onmessage = null
      source.onerror = null
      source.close()
      source = null
    }

    const scheduleReconnect = (): void => {
      if (disposed || retryTimer !== null) return
      attempt += 1
      setReconnectAttempt(attempt)
      retryTimer = setTimeout(() => {
        retryTimer = null
        connect()
      }, backoffDelay(attempt))
    }

    const connect = (): void => {
      if (disposed) return

      const es = new EventSource(`${API_BASE}/api/stream`, { withCredentials: true })
      source = es

      es.onopen = () => {
        if (disposed) return
        attempt = 0
        setReconnectAttempt(0)
        setConnected(true)

        /**
         * Repair the cache after a gap.
         *
         * While the stream was down, every change the API published was lost:
         * frames carry no `id:`, so there is no `Last-Event-ID` to resume from,
         * and a restarted API opens its change streams at "now" rather than at
         * the last token. Nothing else backfills either — the customers query
         * has no refetchInterval, and `refetchOnWindowFocus` / `refetchOnReconnect`
         * do not fire for an SSE-only drop in a tab that never lost focus.
         *
         * The result was a grid showing stale rows indefinitely while the
         * header pill said "Live", with the stats strip (which does poll)
         * disagreeing with it. So on RE-open — not the first open, which the
         * initial fetch already covers — invalidate the views that only the
         * stream keeps current.
         */
        if (hasConnectedBefore.current) {
          void queryClient.invalidateQueries({ queryKey: ['customers'] })
          void queryClient.invalidateQueries({ queryKey: ['stats'] })
          // The run channel is push-only — no poll backs it up — so a gap in
          // the stream is a gap in the log with nothing to close it. Re-seeding
          // is the whole recovery, and it is why the console can otherwise run
          // without polling at all.
          void queryClient.invalidateQueries({ queryKey: ['automation'] })
        }
        hasConnectedBefore.current = true
      }

      es.onmessage = (ev: MessageEvent) => {
        handleFrame(ev.data)
      }

      for (const name of EVENT_NAMES) {
        es.addEventListener(name, (ev: Event) => {
          handleFrame((ev as MessageEvent).data, name)
        })
      }

      es.onerror = () => {
        // EventSource would reconnect on its own, but on a fixed ~3s timer
        // with no ceiling and no jitter. Take the socket back and run our own
        // backoff so a dead API is not hammered by every open console.
        if (es !== source) return
        teardownSource()
        if (disposed) return
        setConnected(false)
        scheduleReconnect()
      }
    }

    connect()

    return () => {
      disposed = true
      if (retryTimer !== null) clearTimeout(retryTimer)
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
      // Whatever is still buffered is stale by the time this component
      // remounts; the refetch-on-mount path is the correct recovery.
      bufferRef.current = emptyBuffer()
      teardownSource()
    }
  }, [queryClient])

  return { connected, lastEventAt, reconnectAttempt }
}
